import { ReactElement } from 'react';
import SectionHeader from '../../components/SectionHeader';
import Button from '../../components/Button';
import ButtonList from '../../components/ButtonList';
import FormInput from '../../components/FormInput';
import FormEntry from '../../components/FormEntry';

import { useForm, FormProvider } from 'react-hook-form';
import { HiOutlineCheckCircle, HiOutlineXMark, HiOutlineInformationCircle } from 'react-icons/hi2';

// Import Tailwind config utilities
import {
  getColors,
  getFontFamilies,
  getScreens,
  getFontSizes,
  // getSpacing,
  getZIndex,
  //getBorderRadius,
  getPrefix,
} from '../../utils/tailwindConfigReader';

interface StyleGuideFormData {
  exampleInput: string;
}

// Helper function to get font descriptions
const getFontDescription = (fontName: string): string => {
  const descriptions: Record<string, string> = {
    main: 'Primary font for body text and general content',
    secondary: 'Secondary font for headings and emphasis',
    header: 'Font for headers and titles',
    tooltip: 'Font for tooltips and small text elements',
    mui: 'Material-UI font for components',
  };

  return descriptions[fontName] || 'Custom font family';
};

export default function StyleGuide(): ReactElement {
  const formMethods = useForm<StyleGuideFormData>({
    defaultValues: {
      exampleInput: '',
    },
  });

  // Access config values using utility functions
  const colors = getColors();
  const fontFamilies = getFontFamilies();
  const screens = getScreens();
  const fontSizes = getFontSizes();
  // const spacing = getSpacing();
  const zIndex = getZIndex();
  // const borderRadius = getBorderRadius();
  const prefix = getPrefix();

  return (
    <FormProvider {...formMethods}>
      <SectionHeader title="Style Guide" />
      <div className="cr-space-y-8">
        {/* Typography Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Typography</h2>
          <div className="cr-space-y-6">
            <div className="cr-space-y-2">
              <h3 className="cr-text-lg cr-font-medium cr-text-blue-900">
                Font Families ({Object.keys(fontFamilies).length} available)
              </h3>
              <div className="cr-grid cr-gap-4 md:cr-grid-cols-2">
                {Object.entries(fontFamilies).map(([fontName, fontValue]) => (
                  <div key={fontName} className="cr-rounded-lg cr-border cr-border-gray-200 cr-p-4">
                    <h4 className={`cr-font-${fontName} cr-mb-2 cr-text-xl cr-font-semibold`}>
                      {fontName.charAt(0).toUpperCase() + fontName.slice(1)} Font
                    </h4>
                    <p className={`cr-font-${fontName} cr-text-base`}>
                      {String(fontValue)} - {getFontDescription(fontName)}
                    </p>
                    <p className="cr-mt-1 cr-text-xs cr-text-gray-500">
                      CSS Class: <code className="cr-rounded cr-bg-gray-100 cr-px-1">cr-font-{fontName}</code>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Colors Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Colors</h2>
          <div className="cr-space-y-6">
            {/* Cirrus Blue Palette */}
            <div>
              <h3 className="cr-mb-4 cr-text-lg cr-font-medium cr-text-blue-900">Cirrus Blue Palette</h3>
              <div className="cr-grid cr-grid-cols-2 cr-gap-4 md:cr-grid-cols-4 lg:cr-grid-cols-7">
                {Object.entries(colors.cirrusBlue || {}).map(([shade, colorValue]) => (
                  <div key={shade} className="cr-text-center">
                    <div
                      className="cr-mb-2 cr-h-16 cr-w-full cr-rounded cr-border cr-border-gray-200"
                      style={{ backgroundColor: String(colorValue) }}
                    ></div>
                    <p className="cr-text-sm cr-font-medium">Cirrus Blue {shade}</p>
                    <p className="cr-text-xs cr-text-gray-600">{String(colorValue)}</p>
                    <p className="cr-text-xs cr-text-gray-400">cr-bg-cirrusBlue-{shade}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Other Color Examples */}
            <div>
              <h3 className="cr-mb-4 cr-text-lg cr-font-medium cr-text-blue-900">Other Colors</h3>
              <div className="cr-grid cr-grid-cols-2 cr-gap-4 md:cr-grid-cols-4">
                <div className="cr-text-center">
                  <div className="cr-mb-2 cr-h-16 cr-w-full cr-rounded cr-bg-gray-200"></div>
                  <p className="cr-text-sm cr-font-medium">Light Gray</p>
                  <p className="cr-text-xs cr-text-gray-600">#E5E7EB</p>
                </div>
                <div className="cr-text-center">
                  <div className="cr-mb-2 cr-h-16 cr-w-full cr-rounded cr-border cr-border-gray-300 cr-bg-white"></div>
                  <p className="cr-text-sm cr-font-medium">White</p>
                  <p className="cr-text-xs cr-text-gray-600">#FFFFFF</p>
                </div>
                <div className="cr-text-center">
                  <div className="cr-mb-2 cr-h-16 cr-w-full cr-rounded cr-bg-green-500"></div>
                  <p className="cr-text-sm cr-font-medium">Success Green</p>
                  <p className="cr-text-xs cr-text-gray-600">#10B981</p>
                </div>
                <div className="cr-text-center">
                  <div className="cr-mb-2 cr-h-16 cr-w-full cr-rounded cr-bg-blue-500"></div>
                  <p className="cr-text-sm cr-font-medium">Blue 500</p>
                  <p className="cr-text-xs cr-text-gray-600">#3B82F6</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Buttons</h2>
          <div className="cr-space-y-4">
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Primary Buttons</h3>
              <ButtonList>
                <Button
                  onClick={() => {
                    console.error('Primary Button not implemented');
                  }}
                  icon={<HiOutlineCheckCircle className="cr-size-7" />}
                >
                  Primary Button
                </Button>
                <Button
                  onClick={() => {
                    console.error('Secondary Button not implemented');
                  }}
                  icon={<HiOutlineXMark className="cr-size-7" />}
                >
                  Secondary Button
                </Button>
                <Button
                  onClick={() => {
                    console.error('Outline Button not implemented');
                  }}
                  icon={<HiOutlineInformationCircle className="cr-size-7" />}
                >
                  Outline Button
                </Button>
              </ButtonList>
            </div>
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Button States</h3>
              <ButtonList>
                <Button
                  onClick={() => {
                    console.error('Loading State not implemented');
                  }}
                  className="cr-opacity-50"
                >
                  Loading State
                </Button>
              </ButtonList>
            </div>
          </div>
        </div>

        {/* Form Elements Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Form Elements</h2>
          <div className="cr-space-y-4">
            <FormEntry label="Text Input">
              <FormInput fieldName="exampleInput" inputType="text" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Email Input">
              <FormInput fieldName="exampleInput" inputType="email" customClass="cr-w-full" />
            </FormEntry>
          </div>
        </div>

        {/* Layout Components Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Layout Components</h2>
          <div className="cr-space-y-4">
            <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
              <div className="cr-mb-4 cr-border-b cr-border-gray-200 cr-pb-2">
                <h3 className="font-montserrat cr-text-2xl-tight cr-font-semibold cr-uppercase cr-text-blue-900">
                  Card Layout
                </h3>
              </div>
              <div className="cr-p-3">
                <p className="cr-text-gray-700">
                  This is an example of a card layout component used throughout the application.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Spacing Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Spacing</h2>
          <div className="cr-space-y-4">
            <div className="cr-space-y-2">
              <div className="cr-bg-blue-100 cr-p-2">Padding 2 (8px)</div>
              <div className="cr-bg-blue-100 cr-p-4">Padding 4 (16px)</div>
              <div className="cr-bg-blue-100 cr-p-6">Padding 6 (24px)</div>
            </div>
            <div className="cr-space-y-2">
              <div className="cr-bg-green-100 cr-p-2">Margin bottom 4 (16px)</div>
              <div className="cr-bg-green-100 cr-p-2">Margin bottom 6 (24px)</div>
              <div className="cr-bg-green-100 cr-p-2">Margin bottom 8 (32px)</div>
            </div>
          </div>
        </div>

        {/* Detailed Tailwind Config Section */}
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">Detailed Tailwind Config Values</h2>
          <div className="cr-space-y-4">
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Prefix: {prefix}</h3>
            </div>
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Available Colors ({Object.keys(colors).length})</h3>
              <div className="cr-grid cr-grid-cols-2 cr-gap-2 cr-text-sm">
                {Object.keys(colors)
                  .slice(0, 10)
                  .map((colorName) => (
                    <div key={colorName} className="cr-rounded cr-bg-gray-100 cr-p-2">
                      {colorName}
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Breakpoints</h3>
              <div className="cr-grid cr-grid-cols-2 cr-gap-2 cr-text-sm">
                {Object.entries(screens).map(([breakpoint, value]) => (
                  <div key={breakpoint} className="cr-rounded cr-bg-gray-100 cr-p-2">
                    {breakpoint}: {String(value)}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Font Sizes</h3>
              <div className="cr-grid cr-grid-cols-2 cr-gap-2 cr-text-sm">
                {Object.entries(fontSizes).map(([size, value]) => (
                  <div key={size} className="cr-rounded cr-bg-gray-100 cr-p-2">
                    {size}: {String(value)}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="cr-mb-2 cr-text-lg cr-font-medium">Z-Index Values</h3>
              <div className="cr-grid cr-grid-cols-2 cr-gap-2 cr-text-sm">
                {Object.entries(zIndex).map(([index, value]) => (
                  <div key={index} className="cr-rounded cr-bg-gray-100 cr-p-2">
                    {index}: {String(value)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
